import { TableCell, TableRow } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { MEDIA_TYPE } from '../../../base/media/constants';
import { getTrackByMediaTypeAndParticipant } from '../../../base/tracks/functions.any';
import { INDICATOR_DISPLAY_THRESHOLD } from '../../../connection-indicator/components/AbstractConnectionIndicator';
import { isTrackStreamingStatusInactive, isTrackStreamingStatusInterrupted } from '../../../connection-indicator/functions';
import MoreStatsParticipant from './MoreStatsParticipant';
import TimeElapsed from './TimeElapsed';
import statsEmitter from '../../../connection-indicator/statsEmitter';
import Tooltip from '../../../base/tooltip/components/Tooltip';
import { makeStyles } from 'tss-react/mui';
const QUALITY_TO_WIDTH = [
    // Full (3 bars)
    {
        colorClass: 'status-high',
        percent: INDICATOR_DISPLAY_THRESHOLD,
        tip: 'connectionindicator.quality.good'
    },
    // 2 bars
    {
        colorClass: 'status-med',
        percent: 10,
        tip: 'connectionindicator.quality.nonoptimal'
    },
    // 1 bar
    {
        colorClass: 'status-low',
        percent: 0,
        tip: 'connectionindicator.quality.poor'
    }
    // Note: we never show 0 bars as long as there is a connection.
];
const useStyles = makeStyles()(theme => {
    return {
        tableRow: {
            '& > *': {
                fontWeight: 500
            },
            '& .MuiTableCell-root': {
                fontWeight: 500
            }
        },
        textEllipsis: {
            maxWidth: 215,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
        }
    };
});
const SpeakerStatsItemV3 = ({ index, participantId, displayName, isDominantSpeaker, dominantSpeakerTime }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const tracks = useSelector((state) => state['features/base/tracks']);
    const [jvbRTT, setJvbRTT] = useState();
    const [intervalTipKey, setIntervalTipKey] = useState();
    const [tipKey, setTipKey] = useState('connectionindicator.quality.good');
    const [percent, setPercent] = useState(undefined);
    const getDisplayConfiguration = (percent) => {
        return QUALITY_TO_WIDTH.find(x => percent >= x.percent)?.tip || '';
    };
    const getConnectionStatusTip = useCallback(() => {
        const _videoTrack = getTrackByMediaTypeAndParticipant(tracks, MEDIA_TYPE.VIDEO, participantId);
        const _isConnectionStatusInactive = isTrackStreamingStatusInactive(_videoTrack);
        const _isConnectionStatusInterrupted = isTrackStreamingStatusInterrupted(_videoTrack);
        let tipKey;
        switch (true) {
            case _isConnectionStatusInterrupted:
                tipKey = 'connectionindicator.quality.lost';
                break;
            case _isConnectionStatusInactive:
                tipKey = 'connectionindicator.quality.inactive';
                break;
            default: {
                if (typeof percent === 'undefined') {
                    // If percentage is undefined then there are no stats available
                    // yet, likely because only a local connection has been
                    // established so far. Assume a strong connection to start.
                    tipKey = 'connectionindicator.quality.good';
                }
                else {
                    // const config = getDisplayConfiguration(percent);
                    tipKey = getDisplayConfiguration(percent);
                }
            }
        }
        return tipKey;
    }, [percent]);
    const handleUpdateStats = (value = {
        jvbRTT: undefined,
        connectionQuality: undefined
    }) => {
        setJvbRTT(value.jvbRTT);
        setPercent(value.connectionQuality);
    };
    useEffect(() => {
        statsEmitter.subscribeToClientStats(participantId, handleUpdateStats);
        return () => {
            statsEmitter.unsubscribeToClientStats(participantId, handleUpdateStats);
        };
    }, [participantId]);
    return (React.createElement(TableRow, { key: participantId, hover: true, role: "checkbox", tabIndex: -1, sx: { cursor: 'auto' }, className: cx(classes.tableRow) },
        React.createElement(TableCell, { padding: "checkbox", align: "center" }, index + 1),
        React.createElement(TableCell, { component: "th", scope: "row", sx: { textAlign: 'center' } },
            React.createElement("span", { className: `userStatus ${isDominantSpeaker && 'active'}` })),
        React.createElement(TableCell, { component: "th", scope: "row" },
            React.createElement(Tooltip, { content: displayName, containerClassName: cx(classes.textEllipsis) },
                React.createElement("span", { className: cx(classes.textEllipsis) }, displayName))),
        React.createElement(TableCell, { component: "th", scope: "row" },
            React.createElement(TimeElapsed, { time: dominantSpeakerTime })),
        React.createElement(TableCell, { component: "th", scope: "row" }, jvbRTT ? jvbRTT + ' ms' : 'N/A'),
        React.createElement(TableCell, { component: "th", scope: "row", sx: {
                color: getConnectionStatusTip() === 'connectionindicator.quality.good' ? '#0054D1' : '#CB002B'
            } }, t(getConnectionStatusTip())),
        React.createElement(TableCell, { component: "th", scope: "row", className: 'detailCell' },
            React.createElement(MoreStatsParticipant, { participantId: participantId }))));
};
export default SpeakerStatsItemV3;
